import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError, setToken, getToken } from "@/lib/api";

const AuthContext = createContext({ user: null, loading: true });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // 401 simply means the token expired — silent. Anything else worth logging.
      if (e?.response?.status !== 401) {
        console.error("/auth/me failed:", e);
      }
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.access_token) setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name, referralSource = null) => {
    const { data } = await api.post("/auth/register", { email, password, name, referral_source: referralSource });
    if (data?.access_token) setToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Network errors during logout are non-blocking; we still clear the local session.
      console.warn("Logout request failed (local session cleared anyway):", e);
    }
    setToken(null);
    setUser(null);
  }, []);

  // Memoise the context value so consumers don't re-render on every parent render.
  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh, setUser }),
    [user, loading, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
export { formatApiError };
